import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateResourceRequest,
  CreateResourceResponse,
  DeleteResourceRequest,
  DeleteResourceResponse,
  UpdateResourceRequest,
  UpdateResourceResponse,
  ViewResourcesByTypeRequest,
  ViewResourcesByTypeResponse
} from './resource.types';

import { CREATING_RESOURCE, DELETING_RESOURCE, RETRIEVING_RESOURCES, UPDATING_RESOURCE } from './resource.constants';

import { requestViewAllCollections } from '../collection/collection.actions';
import {
  requestCreateResource,
  requestDeleteResource,
  requestUpdateResource,
  requestViewResourcesByType
} from './resource.actions';

import {
  getCreateResourceRequest,
  getDeleteResourceRequest,
  getUpdateResourceRequest,
  getViewResourcesByTypeRequest
} from './resource.endpoints';

import { setResources } from '../../slices/programme/plan/plan.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateResourceRequest({ payload }: SagaPayload<CreateResourceRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_RESOURCE));

    const [endpoint, options] = getCreateResourceRequest(requestData);

    const { data }: AxiosResponse<CreateResourceResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_RESOURCE));
  }
}

export function* watchForCreateResourceRequest() {
  yield takeLatest(requestCreateResource.type, performCreateResourceRequest);
}

export function* performUpdateResourceRequest({ payload }: SagaPayload<UpdateResourceRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_RESOURCE));

    const [endpoint, options] = getUpdateResourceRequest(requestData);

    const { data }: AxiosResponse<UpdateResourceResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_RESOURCE));
  }
}

export function* watchForUpdateResourceRequest() {
  yield takeLatest(requestUpdateResource.type, performUpdateResourceRequest);
}

export function* performDeleteResourceRequest({ payload }: SagaPayload<DeleteResourceRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_RESOURCE));

    const [endpoint, options] = getDeleteResourceRequest(requestData);

    const { data }: AxiosResponse<DeleteResourceResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_RESOURCE));
  }
}

export function* watchForDeleteResourceRequest() {
  yield takeLatest(requestDeleteResource.type, performDeleteResourceRequest);
}

export function* performViewResourcesByTypeRequest({ payload }: SagaPayload<ViewResourcesByTypeRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_RESOURCES));

    const [endpoint, options] = getViewResourcesByTypeRequest(requestData);

    const { data }: AxiosResponse<ViewResourcesByTypeResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setResources(data.resources));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_RESOURCES));
  }
}

export function* watchForViewResourcesByTypeRequest() {
  yield takeLatest(requestViewResourcesByType.type, performViewResourcesByTypeRequest);
}

export default function* resourceSaga() {
  yield all([
    watchForCreateResourceRequest(),
    watchForUpdateResourceRequest(),
    watchForDeleteResourceRequest(),
    watchForViewResourcesByTypeRequest()
  ]);
}
